/*
 * Lightweight scroll-reveal + entrance animation primitive.
 * Replaces framer-motion's `motion.*` + `whileInView`/`animate` usage with a
 * tiny IntersectionObserver + CSS-transition implementation. This keeps the
 * critical JS bundle small (no framer-motion) while preserving the visual
 * "fade/slide in on view" behavior used across the marketing site.
 *
 * Honors `prefers-reduced-motion` automatically via CSS (see index.css).
 */
import * as React from "react";

type Direction = "up" | "down" | "left" | "right" | "none";

type RevealProps<T extends React.ElementType> = {
  as?: T;
  /** Animate immediately on mount (above-the-fold) instead of on scroll. */
  immediate?: boolean;
  /** Animation start direction. */
  from?: Direction;
  /** Delay in seconds. */
  delay?: number;
  /** Duration in seconds. */
  duration?: number;
  children?: React.ReactNode;
  className?: string;
} & Omit<React.ComponentPropsWithoutRef<T>, "as" | "children" | "className">;

export function Reveal<T extends React.ElementType = "div">({
  as,
  immediate = false,
  from = "up",
  delay = 0,
  duration = 0.6,
  className = "",
  style,
  children,
  ...rest
}: RevealProps<T>) {
  const Tag = (as || "div") as React.ElementType;

  // Above-the-fold elements (immediate=true) must never start at opacity:0.
  // The opacity:0→1 transition would delay LCP by delay+duration seconds.
  // Render without the reveal class so content is painted immediately.
  if (immediate) {
    return (
      <Tag className={className} style={style as React.CSSProperties} {...rest}>
        {children}
      </Tag>
    );
  }

  // Below-fold: use IntersectionObserver-driven fade/slide reveal.
  return <ScrollReveal as={as} from={from} delay={delay} duration={duration} className={className} style={style} {...rest}>{children}</ScrollReveal>;
}

function ScrollReveal<T extends React.ElementType = "div">({
  as,
  from = "up",
  delay = 0,
  duration = 0.6,
  className = "",
  style,
  children,
  ...rest
}: Omit<RevealProps<T>, "immediate">) {
  const Tag = (as || "div") as React.ElementType;
  const ref = React.useRef<HTMLElement | null>(null);
  const [shown, setShown] = React.useState(false);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShown(true);
            observer.disconnect();
            break;
          }
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -10% 0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      data-reveal={from}
      data-shown={shown ? "true" : "false"}
      className={`reveal ${className}`}
      style={{
        ...(style as React.CSSProperties),
        transitionDelay: delay ? `${delay}s` : undefined,
        transitionDuration: `${duration}s`,
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

export default Reveal;
